/**
 * UI Watchdog — detects frozen UI and auto-reloads.
 *
 * Posts a heartbeat every 5s. If the check timer detects 3 missed
 * beats (15s), the UI is considered frozen and force-reloads.
 *
 * In CW Browser: also notifies Electron main process.
 * In Web mode: uses a secondary timer as fallback.
 */

import { trackFreeze } from "./healthReporter";
import useSyncProgressStore from "../stores/useSyncProgressStore";
import { releaseInstanceLock } from "../components/SingleInstanceGuard";

const HEARTBEAT_INTERVAL = 5000;  // 5s between beats
const FREEZE_THRESHOLD = 15000;   // 15s = frozen
let lastBeat = Date.now();
let heartbeatTimer = null;
let checkTimer = null;
let started = false;

// [FREEZE-DIAG] Rolling buffer of long tasks (>50ms) reported by the browser's
// Long Tasks API. A genuine main-thread block (e.g. a synchronous sync loop)
// shows up here with duration + attribution; a false "freeze" caused by the
// browser throttling timers on a backgrounded/minimized window does NOT — the
// two look identical to the heartbeat/check timers alone, so this is what lets
// us tell them apart after the fact.
const longTaskLog = [];
const MAX_LONG_TASKS = 300;

function recordLongTask(entry) {
  const record = {
    startMs: Math.round(entry.startTime),
    durationMs: Math.round(entry.duration),
    name: entry.name,
    attribution: (entry.attribution || []).map((a) => ({
      containerType: a.containerType,
      containerSrc: a.containerSrc,
      containerName: a.containerName,
    })),
  };
  longTaskLog.push(record);
  if (longTaskLog.length > MAX_LONG_TASKS) longTaskLog.shift();
}

function startLongTaskObserver() {
  if (typeof PerformanceObserver === "undefined") {
    console.warn("[FREEZE-DIAG] PerformanceObserver unavailable — long-task attribution disabled");
    return;
  }
  try {
    const supported = PerformanceObserver.supportedEntryTypes || [];
    if (!supported.includes("longtask")) {
      console.warn("[FREEZE-DIAG] 'longtask' entry type not supported in this environment");
      return;
    }
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach(recordLongTask);
    });
    observer.observe({ type: "longtask", buffered: true });
  } catch (err) {
    console.warn("[FREEZE-DIAG] Failed to start long-task observer:", err?.message);
  }
}

// [FREEZE-DIAG] Track tab visibility transitions. A minimized/backgrounded
// window gets its timers throttled by the browser/OS — that can produce the
// exact same symptom (heartbeat timer doesn't fire for >15s) as a real freeze,
// with zero long tasks recorded, because nothing was actually blocking JS.
const visibilityLog = [];
function recordVisibility() {
  const entry = { at: Date.now(), state: document.visibilityState, hidden: document.hidden };
  visibilityLog.push(entry);
  if (visibilityLog.length > 100) visibilityLog.shift();
}

function beat() {
  lastBeat = Date.now();
}

function check() {
  const elapsed = Date.now() - lastBeat;
  if (elapsed > FREEZE_THRESHOLD) {
    const windowStartWall = lastBeat;
    const windowEndWall = Date.now();

    // Long task timestamps are relative to performance.timeOrigin; convert to
    // wall-clock (Date.now()-comparable) to filter to just this freeze window.
    const longTasksInWindow = longTaskLog.filter((t) => {
      const absStart = performance.timeOrigin + t.startMs;
      return absStart >= windowStartWall - 2000 && absStart <= windowEndWall + 2000;
    });
    // Lookback must cover the FULL freeze window, not a fixed short span — a
    // freeze can last tens of seconds, and the hidden transition that caused it
    // may have happened well before a short fixed cutoff (that was the original
    // bug here: a 76s freeze with a 10s lookback missed the transition entirely).
    const recentVisibility = visibilityLog.filter((v) => v.at >= windowStartWall - elapsed - 5000 && v.at <= windowEndWall + 2000);

    const totalLongTaskMsInWindow = longTasksInWindow.reduce((s, t) => s + t.durationMs, 0);
    const hiddenNow = document.hidden;
    const wasHiddenDuringWindow = hiddenNow || recentVisibility.some((v) => v.hidden);
    // A genuine main-thread block accounts for close to the full elapsed gap in
    // measured long-task time (Long Tasks API records ANY JS execution >50ms, so
    // continuous blocking JS shows up as long tasks summing near the full gap).
    // If long-task time covers only a small fraction of the elapsed gap, the JS
    // engine wasn't actually busy for most of it — the far more likely explanation
    // is the browser throttling timers on a hidden/backgrounded window, not a
    // real freeze, REGARDLESS of whether a few incidental short long tasks also
    // happened to occur (e.g. right as focus returned).
    const longTaskCoversElapsed = totalLongTaskMsInWindow >= elapsed * 0.5;
    const looksLikeBackgroundThrottling = wasHiddenDuringWindow && !longTaskCoversElapsed;

    const diag = {
      elapsedMs: elapsed,
      visibilityStateNow: document.visibilityState,
      hiddenNow,
      recentVisibilityTransitions: recentVisibility,
      longTasksDuringFreeze: longTasksInWindow,
      longTaskCount: longTasksInWindow.length,
      totalLongTaskMsInWindow,
      looksLikeBackgroundThrottling,
      syncPercent: useSyncProgressStore.getState().percent,
      memory: performance.memory
        ? {
            usedMB: Math.round(performance.memory.usedJSHeapSize / 1048576),
            totalMB: Math.round(performance.memory.totalJSHeapSize / 1048576),
            limitMB: Math.round(performance.memory.jsHeapSizeLimit / 1048576),
          }
        : null,
      url: window.location.href,
    };

    if (looksLikeBackgroundThrottling) {
      // Not a real freeze — the tab was hidden/minimized and the browser paused
      // timers, which is expected behavior, not a bug. Reloading here would only
      // needlessly tear down and reconnect the WebSocket (and could repeat every
      // ~15s for as long as the window stays backgrounded). Re-arm silently
      // instead; normal timers resume the instant the tab becomes visible again.
      console.warn(
        `[FREEZE-DIAG] Skipping reload — ${Math.round(elapsed / 1000)}s gap looks like browser timer throttling on a ` +
          `hidden/backgrounded window (only ${totalLongTaskMsInWindow}ms of real long-task time recorded across the gap), not a real freeze.`,
        diag
      );
      lastBeat = Date.now();
      return;
    }

    console.error(`[Watchdog] UI frozen for ${Math.round(elapsed / 1000)}s — reloading`);
    console.error("[FREEZE-DIAG] Freeze context:", diag);

    if (diag.longTaskCount > 0) {
      console.warn(
        `[FREEZE-DIAG] ${diag.longTaskCount} long task(s) totaling ${diag.totalLongTaskMsInWindow}ms recorded during this window — ` +
          "this IS a real main-thread block. See attribution above for the offending script."
      );
    } else {
      console.warn("[FREEZE-DIAG] No long tasks recorded and tab was visible throughout — inconclusive; possible GC pause or native/IPC stall.");
    }

    trackFreeze();

    // Log to observability before reload
    try {
      if (window.electronAPI?.observability?.log) {
        window.electronAPI.observability.log("error", "freeze", `UI frozen for ${Math.round(elapsed / 1000)}s`, {
          lastBeat: new Date(lastBeat).toISOString(),
          ...diag,
        });
      }
    } catch {}

    // Release the single-instance lock ourselves before reloading. The event
    // loop is still catching up from whatever caused the freeze, so pagehide's
    // own cleanup may not run before the reloaded page claims the lock — this
    // guarantees a clean handoff instead of racing SingleInstanceGuard's own
    // staleness window (STALE_MS deliberately equals this freeze threshold,
    // so that race can go either way).
    try {
      releaseInstanceLock();
    } catch {
      // Never let lock cleanup block the recovery reload.
    }

    // Force reload
    window.location.reload();
  }
}

export function startWatchdog() {
  if (started) return;
  started = true;
  lastBeat = Date.now();

  startLongTaskObserver();
  document.addEventListener("visibilitychange", recordVisibility);
  recordVisibility();

  // Heartbeat: proves the main thread is responsive
  heartbeatTimer = setInterval(beat, HEARTBEAT_INTERVAL);

  // Check: runs slightly offset, detects missed beats
  checkTimer = setInterval(check, HEARTBEAT_INTERVAL + 1000);

}

